import React, { useEffect, useRef, useState } from 'react';
import { errorMessage } from '../api';
import { Dialog, Field, FormError, useResource } from '../components';
import { anfrageSenden, MEHR_ANFRAGE_WEGE, type MeineAnfrage } from './mehrAnfrageApi';
import { anfrageDatum, MEHR_ANFRAGE_TEXTE } from './mehrAnfrageTexte';

// Profil-Nachbau F-15 Punkt 6 (output/profil-team-20261014/BAU-GELD.md 1.6, Teil „Mitglied“; PF-759): „Mehr Kontingent anfragen …“
// für ein Mitglied, aus der Sperrkarte der Recherche (components/ResearchAccess.tsx, Grund PERSON oder FIRMA) und unter dem
// persönlichen Balken ab 80 % (pages/einstellungen/Nutzungsbalken.tsx). Der Knopf fragt GET /billing/kontingent-anfragen/meine
// einmal, wenn er gezeichnet würde, und erscheint nur bei darf: true; ein Fehler (auch 401/403) blendet ihn still aus. Der Dialog
// (Fokusfalle, Escape, der Fokus kehrt zurück: Dialog in components.tsx) schickt die Begründung; ist eine Anfrage offen, ersetzt die
// neue Begründung die bisherige. Die Begründung steht nur als Text; der Server speichert sie verschlüsselt.

const T = MEHR_ANFRAGE_TEXTE;

/** Der Knopf mit seinem Dialog; klein: Textlink unter dem Satz des Balkens statt Knopf. */
export function MehrAnfrageKnopf({ firmaName, klein = false }: { firmaName?: string | null; klein?: boolean }) {
  const [version, setVersion] = useState(0), [offen, setOffen] = useState(false), [meldung, setMeldung] = useState(''), [zurueck, setZurueck] = useState(false);
  const meine = useResource<MeineAnfrage>(MEHR_ANFRAGE_WEGE.meine, version);
  // PF-SL1-geld-1 (Schlusslauf 1, kontingent-anfrage-ui): nach dem Senden lädt der Knopf seinen Stand neu. Solange bleibt er mit dem
  // letzten Stand stehen, sonst hinge er beim Schließen des Dialogs ab und der Fokus läge auf body; danach bekommt er ihn ausdrücklich.
  const zuletzt = useRef<MeineAnfrage | null>(null);
  if (meine.data) zuletzt.current = meine.data;
  const stand = meine.data ?? (meine.loading ? zuletzt.current : null);
  // PF-SL6-anfrage-1 (Schlusslauf 6, kontingent-anfrage-ui:88-90): öffnet man den Dialog, bevor der neue Stand da ist, beginnt er mit
  // dem alten, und useState im Dialog übernimmt die bisherige Begründung nur beim ersten Zeichnen. Der Schlüssel aus Anfrage und
  // Begründung lässt ihn mit dem neuen Stand neu beginnen – nach dem ersten Senden (neue Anfrage) wie nach „aktualisieren“ (neuer Text).
  const knopf = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!zurueck || offen || !knopf.current) return;
    knopf.current.focus();
    setZurueck(false);
  }, [zurueck, offen, stand]);
  const erfolg = meldung && <p className="adm-success" role="status">{meldung}</p>;
  if (!stand?.darf) return erfolg || null;
  return <>
    <p><button ref={knopf} type="button" className={klein ? 'adm-text-link' : 'adm-button'} onClick={() => { setMeldung(''); setOffen(true); }}>{T.knopf}</button></p>
    {erfolg}
    {offen && <MehrAnfrageDialog key={stand.offen ? `${stand.offen.id}:${stand.offen.text ?? ''}` : 'neu'} firmaName={firmaName} stand={stand} onClose={() => setOffen(false)}
      onGesendet={satz => { setOffen(false); setMeldung(satz); setZurueck(true); setVersion(v => v + 1); }} />}
  </>;
}

/** „Mehr Kontingent anfragen“: Satz, Begründung (optional, höchstens 500 Zeichen, mit Zähler), Knöpfe; mit offener Anfrage oben der
 *  Satz dazu, die bisherige Begründung im Feld und „Anfrage aktualisieren“. Die Sätze des Servers (409 ANFRAGEN_GRENZE, 403
 *  ANFRAGE_NICHT_MOEGLICH) stehen im Dialog. */
export function MehrAnfrageDialog({ firmaName, stand, onClose, onGesendet }: { firmaName?: string | null; stand: MeineAnfrage; onClose: () => void;
  onGesendet: (satz: string) => void }) {
  const bisher = stand.offen?.text ?? '';
  const [text, setText] = useState(bisher), [busy, setBusy] = useState(false), [fehler, setFehler] = useState<unknown>(null);
  const aktualisieren = stand.offen !== null;
  const senden = async (event: React.FormEvent) => {
    event.preventDefault(); setBusy(true); setFehler(null);
    try { await anfrageSenden(text.trim()); onGesendet(aktualisieren ? T.aktualisiert : T.gesendet); }
    catch (error) { setFehler(error); setBusy(false); }
  };
  return <Dialog title={T.titel} onClose={onClose} busy={busy} verwerfenFragen={text !== bisher}>
    <form onSubmit={event => void senden(event)}>
      {stand.offen && <p>{T.offen(anfrageDatum(stand.offen.am))}</p>}
      <p>{T.satz(firmaName)}</p>
      <Field label={T.feld} hint={<>{T.hinweis} {T.zaehler(text.length)}</>}>
        <textarea value={text} maxLength={500} rows={4} placeholder={T.platzhalter} onChange={event => { setText(event.target.value); setFehler(null); }} /></Field>
      <FormError message={fehler ? errorMessage(fehler) : ''} />
      <footer>
        <button type="button" className="adm-button" disabled={busy} onClick={onClose}>{T.abbrechen}</button>
        <button type="submit" className="adm-button adm-primary" disabled={busy}>{aktualisieren ? T.aktualisieren : T.senden}</button>
      </footer>
    </form>
  </Dialog>;
}
